import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { MapPin, Plus } from '@phosphor-icons/react';

import {
  Button,
  Options,
  OptionRow,
  Pill,
  Radio,
  Spinner,
} from '../../design-system/components/desktop';
import { Icon } from '../../design-system/components/Icon';
import addressModule from '../../api/modules/address.module';

/**
 * The saved-address picker.
 *
 * Behaviour is unchanged: it renders nothing when the buyer has no saved
 * addresses, auto-selects the default (or the first) on mount, and "Use a
 * different address" hands `null` back so `BillingForm` falls through to the
 * manual fields.
 *
 * Converted to the library `Options` / `OptionRow` / `Radio`. Three things come
 * with that and none of them were true before:
 * - The whole row is the hit target, because `OptionRow` is a `<label>` and the
 *   input is associated by containment.
 * - A CHOSEN row is an accent tint plus an accent inset ring, never an ink
 *   fill, because selection is state and ink is command.
 * - `:focus-within` puts the ring on the row, so keyboard focus on the 18px
 *   radio is visible at row scale.
 *
 * `SavedAddressSelector.css` is deleted; the loading line is the shared
 * `Spinner` rather than the string "Loading addresses...".
 */
const SavedAddressSelector = ({ onSelectAddress, selectedAddressId = null }) => {
  const [showNewAddress, setShowNewAddress] = useState(!selectedAddressId);

  const { data: addresses = [], isLoading } = useQuery({
    queryKey: ['userAddresses'],
    queryFn: async () => {
      const res = await addressModule.getAddresses();
      if (!res.success) return [];
      return res.data || [];
    },
  });

  useEffect(() => {
    if (!selectedAddressId && addresses.length > 0 && !showNewAddress) {
      const defaultAddress = addresses.find((a) => a.is_default) || addresses[0];
      if (defaultAddress) {
        onSelectAddress(defaultAddress);
        setShowNewAddress(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addresses.length, selectedAddressId, showNewAddress]);

  const formatAddress = (address) =>
    [
      address.address_line1,
      address.address_line2,
      address.city,
      address.province,
      address.postal_code,
    ]
      .filter(Boolean)
      .join(', ');

  const handleSelectAddress = (address) => {
    onSelectAddress(address);
    setShowNewAddress(false);
  };

  const handleUseNewAddress = () => {
    setShowNewAddress(true);
    onSelectAddress(null);
  };

  if (isLoading) {
    return <Spinner size="sm">Loading your saved addresses</Spinner>;
  }

  // No saved addresses means there is nothing to pick between, so the picker
  // does not appear at all and the manual fields stand on their own.
  if (addresses.length === 0) return null;

  if (showNewAddress) {
    return (
      <div className="dsa-checkout__addrswitch">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setShowNewAddress(false);
            if (addresses.length > 0) {
              const defaultAddress = addresses.find((a) => a.is_default) || addresses[0];
              handleSelectAddress(defaultAddress);
            }
          }}
        >
          Use a saved address
        </Button>
      </div>
    );
  }

  return (
    <div className="dsa-checkout__addresses">
      <p className="dsk-label dsa-checkout__addrlabel">Select a saved address</p>

      <Options>
        {addresses.map((address) => {
          const addressId = String(address.id);
          const selectedId = selectedAddressId ? String(selectedAddressId) : null;
          const isSelected = selectedId === addressId;

          return (
            <OptionRow
              key={address.id}
              control={
                <Radio
                  name="saved-address"
                  value={addressId}
                  checked={isSelected}
                  onChange={() => handleSelectAddress(address)}
                />
              }
            >
              <span className="dsk-option-row__body">
                <span className="dsk-option-row__title">
                  <Icon as={MapPin} size="sm" /> {address.title || 'Address'}
                  {address.is_default ? <Pill tone="ink">Default</Pill> : null}
                </span>
                <span className="dsk-option-row__sub">{formatAddress(address)}</span>
              </span>
            </OptionRow>
          );
        })}
      </Options>

      <div className="dsa-checkout__addrswitch">
        <Button variant="ghost" size="sm" onClick={handleUseNewAddress}>
          <Icon as={Plus} size="sm" /> Use a different address
        </Button>
      </div>
    </div>
  );
};

export default SavedAddressSelector;
